import { useEffect } from 'react';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { InAppContentDefinition, MESSAGE_FORMAT } from 'modules/graphql/types';
import { colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { selectUserProjectId } from 'core/userContext/selectors';
import { CompatibilityTooltip } from '../CompatibilityTooltip';
import {
  formatHexForState,
  formatHexForUI,
  formatOpacityForState,
  formatOpacityForUI,
} from '../utils';
import { SettingsReadOnlyText } from '../../common/EditorContentContainer.styled';
import { CheckboxField } from '../../common';
import { getIsEditMode, getIsInvalidHexColor } from '../../common/utils';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useLocalStorageOrUseState } from '../../hooks/useLocalStorageOrUseState';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';

export const HEX_COLOR_DATA_TEST = 'hex-color-input';
export const OPACITY_DATA_TEST = 'opacity-input';
export const USE_BG_DATA_TEST = 'use-bg-overlay-input';

type ColorPreviewProps = {
  hexColor: string | undefined;
  alpha: number | undefined;
  isEditMode: boolean;
};

const PickerWrapper = styled.div`
  > * > input {
    width: ${pxToRem(50)};
  }
`;

const ColorPreviewWrapper = styled.div`
  border: ${spacing.single} solid ${colors.grey15};
  border-radius: ${spacing.quarter};
  padding: ${spacing.quarter};
`;

const BgOverlayContainer = styled.section`
  column-gap: ${spacing.half};
  display: flex;
  align-items: center;
  flex: 0 0 auto;
`;

const ColorPreview = styled.div<ColorPreviewProps>`
  width: ${(props) => (props.isEditMode ? spacing.double : pxToRem(20))};
  height: ${(props) => (props.isEditMode ? spacing.double : pxToRem(20))};
  background: ${(props) => props.hexColor};
  border-radius: ${spacing.quarter};
  opacity: ${(props) => props.alpha};
  margin: auto;
`;

const CheckboxWrapper = styled.div`
  margin-bottom: ${spacing.base};
`;

export const BackgroundOverlay = () => {
  const userProjectId = useSelector(selectUserProjectId);
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const inAppMediumData = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const isNewTemplate = !inAppMediumData?.html && !inAppMediumData?.payload;
  const { inAppDisplaySettings, messageFormat } = inAppMediumData;
  const { bgColor } = inAppDisplaySettings;

  const [hexColor, setHexColor] = useLocalStorageOrUseState(
    `proj-${userProjectId}-inapp-bgoverlay-hex`,
    undefined,
    inAppDisplaySettings?.bgColor?.hex,
    isNewTemplate,
  );
  const [alpha, setAlpha] = useLocalStorageOrUseState<undefined | number>(
    `proj-${userProjectId}-inapp-bgoverlay-alpha`,
    undefined,
    inAppDisplaySettings?.bgColor?.alpha,
    isNewTemplate,
  );
  const [useBgOverlay, setUseBgOverlay] = useLocalStorageOrUseState<boolean>(
    `proj-${userProjectId}-inapp-use-bg-overlay`,
    false,
    !!inAppDisplaySettings?.bgColor,
    isNewTemplate,
  );

  useEffect(() => {
    if (hexColor) {
      setCurrentMediumField('inAppDisplaySettings', {
        ...inAppDisplaySettings,
        bgColor: {
          ...inAppDisplaySettings.bgColor,
          hex: hexColor,
        },
      });
    }
  }, [hexColor]);

  useEffect(() => {
    if (alpha) {
      setCurrentMediumField('inAppDisplaySettings', {
        ...inAppDisplaySettings,
        bgColor: {
          ...inAppDisplaySettings.bgColor,
          alpha,
        },
      });
    }
  }, [alpha]);

  useEffect(() => {
    if (!useBgOverlay) {
      setCurrentMediumField('inAppDisplaySettings', {
        ...inAppDisplaySettings,
        bgColor: null,
      });
      setAlpha(undefined);
      setHexColor(undefined);
    }
  }, [useBgOverlay]);

  /**
   * Whenever switching edit mode/non-edit mode, update UI using the actual data.
   * */
  useEffect(() => {
    setCurrentMediumField('inAppDisplaySettings', {
      ...inAppDisplaySettings,
      bgColor: inAppDisplaySettings.bgColor,
    });
    setAlpha(inAppDisplaySettings.bgColor?.alpha);
    setHexColor(inAppDisplaySettings.bgColor?.hex);
    if (inAppDisplaySettings.bgColor) {
      setUseBgOverlay(true);
    } else {
      setUseBgOverlay(false);
    }
  }, [isEditModeVisible]);

  const hexColorHasError = getIsInvalidHexColor(hexColor);

  return (
    <>
      {messageFormat !== MESSAGE_FORMAT.Json && (
        <FormField
          isHorizontalLayout
          labelText={SETTINGS_DISPLAY_CONTENT.backgroundOverlay.labelText}
        >
          {isEditModeVisible && (
            <>
              <CheckboxWrapper>
                <CheckboxField
                  label={
                    <>
                      {SETTINGS_DISPLAY_CONTENT.backgroundOverlay.compatibilityLabel}
                      <CompatibilityTooltip />
                    </>
                  }
                  data-test={USE_BG_DATA_TEST}
                  onClick={() => setUseBgOverlay(!useBgOverlay)}
                  status={useBgOverlay}
                />
              </CheckboxWrapper>
              {useBgOverlay && (
                <>
                  <BgOverlayContainer>
                    <ColorPreviewWrapper>
                      <ColorPreview alpha={alpha} hexColor={hexColor} isEditMode={isEditMode} />
                    </ColorPreviewWrapper>
                    <PickerWrapper>
                      <InputText
                        Prefix={SETTINGS_DISPLAY_CONTENT.backgroundOverlay.hexColorPrefix}
                        data-test={HEX_COLOR_DATA_TEST}
                        hasError={hexColorHasError}
                        id="hexColor"
                        onChange={(newValue) => setHexColor(formatHexForState(newValue))}
                        placeholder={SETTINGS_DISPLAY_CONTENT.backgroundOverlay.hexColorPlaceholder}
                        type="text"
                        isDisabled={!isEditModeVisible}
                        value={formatHexForUI(hexColor)}
                        width={'110px'}
                      />
                    </PickerWrapper>
                    <PickerWrapper>
                      <InputText
                        Prefix={SETTINGS_DISPLAY_CONTENT.backgroundOverlay.opacityPrefix}
                        data-test={OPACITY_DATA_TEST}
                        hasError={!alpha}
                        id="opacity"
                        isDisabled={!isEditModeVisible}
                        onChange={(newValue) => setAlpha(formatOpacityForState(newValue))}
                        placeholder={SETTINGS_DISPLAY_CONTENT.backgroundOverlay.opacityPlaceholder}
                        type="text"
                        value={formatOpacityForUI(alpha)}
                        width={'110px'}
                      />
                    </PickerWrapper>
                  </BgOverlayContainer>
                </>
              )}
            </>
          )}
          {!isEditModeVisible && (
            <SettingsReadOnlyText isUnset={!useBgOverlay} data-test="background-overlay-ro">
              {bgColor ? (
                <BgOverlayContainer>
                  <div>
                    <ColorPreview
                      alpha={bgColor.alpha}
                      hexColor={bgColor.hex}
                      isEditMode={isEditMode}
                    />
                  </div>
                  <div>{`${bgColor.hex} (${formatOpacityForUI(bgColor.alpha)}%)`}</div>
                </BgOverlayContainer>
              ) : (
                'Off'
              )}
            </SettingsReadOnlyText>
          )}
        </FormField>
      )}
    </>
  );
};
